import type { QueryClient } from '@tanstack/react-query';
import type { getConfigByKey } from '@tn/configs';
import { LOGIN_SESSION_COOKIE } from '@tn/foundation/utils/constants/common';
import { setCookie } from 'cookies-next';

import { getUser } from '../helpers';
import { getEnabledMvpdSignInHandoff } from '../mvpd/signInHandoff';
import { getEnabledPartnerBindContext } from '../partner';
import { runPostAuthClientSetup } from '../runPostAuthClientSetup';

type RunPostSignInClientSetupParams = {
    queryClient: QueryClient;
    getConfigByKey: ReturnType<typeof getConfigByKey>;
};

/** Runs post-login client steps after signIn succeeds and CBS_COM is set. */
export async function runPostSignInClientSetup({
    queryClient,
    getConfigByKey,
}: RunPostSignInClientSetupParams): Promise<void> {
    const skipProfileRefresh =
        getEnabledMvpdSignInHandoff(getConfigByKey) || getEnabledPartnerBindContext(getConfigByKey);

    const profileRefreshed = await runPostAuthClientSetup({
        queryClient,
        getConfigByKey,
        flow: 'signIn',
        skipProfileRefresh: Boolean(skipProfileRefresh),
    });

    // Handoff sign-ins skip the refresh and a failed refresh is swallowed upstream; either way
    // the cache still holds the pre-sign-in user, so its profile count means nothing here.
    if (!profileRefreshed) {
        return;
    }

    // Legacy login (UserProfileCookie::setCookieIfHasMultiProfiles) sets this cookie so the
    // next page boot opens Who's Watching; legacy home and the TN boot hook both read it and
    // the picker clears it on selection.
    const profileCount = getUser(queryClient)?.userFullProfile?.accountProfiles?.length ?? 0;
    if (profileCount > 1) {
        setCookie(LOGIN_SESSION_COOKIE, '1', { maxAge: 60 * 60 * 24, sameSite: 'lax', path: '/' });
    }
}
